import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import { useEnrollmentStore } from '@/stores'
import {
  hydrateEnrollmentForm,
  persistEnrollmentHydration
} from '../../../../utils/enrollmentHydration'
import { resolveRestoredStep } from '../../utils/utils'
import type {
  ApplyHydrationArgs,
  FlowSessionState,
  HydrationStoreBindings
} from '../../types'
import type { EligibilityFormData } from '../../../../components/eligibilitySteps/types'
import type { EnrollmentFieldEditability } from '../../../../utils/enrollmentHydration'

type UseApplyHydrationArgs = {
  formDataRef: MutableRefObject<EligibilityFormData>
  goToStep: (step: number) => void
  setFormData: Dispatch<SetStateAction<EligibilityFormData>>
  setFieldEditability: Dispatch<SetStateAction<EnrollmentFieldEditability>>
  sessionState: Pick<
    FlowSessionState,
    | 'setEnrollmentId'
    | 'setEnrollmentCompleted'
    | 'setSessionToken'
    | 'setSignatureCompleted'
  >
  storeBindings: Pick<HydrationStoreBindings, 'updateUploadedDocumentFiles'>
  syncStoreFormData: (next: EligibilityFormData) => void
  totalSteps: number
}

export function useApplyHydration({
  formDataRef,
  goToStep,
  setFormData,
  setFieldEditability,
  sessionState,
  storeBindings,
  syncStoreFormData,
  totalSteps
}: UseApplyHydrationArgs) {
  const { setEnrollmentId, setEnrollmentCompleted, setSessionToken, setSignatureCompleted } =
    sessionState
  const { updateUploadedDocumentFiles } = storeBindings

  return useCallback(
    ({
      profile,
      enrollment,
      syncStep = true,
      stepSyncMode = 'resolved'
    }: ApplyHydrationArgs) => {
      const result = hydrateEnrollmentForm({
        profile,
        enrollment,
        previous: formDataRef.current
      })

      formDataRef.current = result.formData
      setFormData(result.formData)
      setFieldEditability(result.fieldEditability)
      syncStoreFormData(result.formData)
      persistEnrollmentHydration(result.formData, result)

      if (result.sessionToken) {
        setSessionToken(result.sessionToken)
      }
      if (typeof result.enrollmentId === 'number') {
        setEnrollmentId(result.enrollmentId)
      }
      if (result.signatureCompleted) {
        setSignatureCompleted(true)
      }
      if (result.enrollmentCompleted) {
        setEnrollmentCompleted(true)
      }
      if (Object.keys(result.uploadedDocumentFiles).length > 0) {
        updateUploadedDocumentFiles(result.uploadedDocumentFiles)
      }
      if (
        syncStep &&
        typeof result.currentStep === 'number' &&
        result.currentStep >= 0 &&
        result.currentStep <= totalSteps
      ) {
        const nextStep =
          stepSyncMode === 'remote'
            ? result.currentStep
            : resolveRestoredStep({
                localStep: useEnrollmentStore.getState().currentStep,
                remoteStep: result.currentStep,
                maxRestorableStep: result.maxRestorableStep,
                totalSteps
              })

        goToStep(nextStep)
      }
    },
    [
      formDataRef,
      goToStep,
      setEnrollmentCompleted,
      setEnrollmentId,
      setFieldEditability,
      setFormData,
      setSessionToken,
      setSignatureCompleted,
      syncStoreFormData,
      totalSteps,
      updateUploadedDocumentFiles
    ]
  )
}
